import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { Animated, Easing, View } from 'react-native';
import { BLUE_GREY_200, GREEN_400, LILAC_600, LILAC_700, WHITE } from '../../../../constants/colors';
import { useBreakpoint } from '../../../../hooks';
import { StateConfigContext } from '../../../../providers';
import { getTestId } from '../../../../utils';
import { DynamicSvg } from '../../../dynamic-svg';
import { Progress } from '../../../progress';
import { Typography } from '../../../typography';
import { getStepContainerStyles, getStepperProps, getStepperStyles } from '../../StepperStyles';
export var Step = props => {
  var _a, _b, _c, _d, _e, _f, _g;
  var step = props.step;
  var stateContext = useContext(StateConfigContext);
  var stepperProps = getStepperProps(props, stateContext);
  var stepperStyles = getStepperStyles(stepperProps);
  var size = stepperProps.size,
    _stepperProps$testID = stepperProps.testID,
    testID = _stepperProps$testID === void 0 ? 'Step' : _stepperProps$testID,
    theme = stepperProps.theme,
    _stepperProps$showMul = stepperProps.showMultiLevelStepper,
    showMultiLevelStepper = _stepperProps$showMul === void 0 ? false : _stepperProps$showMul,
    style = stepperProps.style,
    isLastChild = stepperProps.isLastChild,
    lastItemConnectorHeight = stepperProps.lastItemConnectorHeight,
    delay = stepperProps.delay,
    showAnimation = stepperProps.showAnimation;
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var _step$state = step.state,
    state = _step$state === void 0 ? 'default' : _step$state,
    stepsBackgroundColor = step.stepsBackgroundColor,
    stepNumber = step.stepNumber,
    progressPercent = step.progressPercent,
    progressColor = step.progressColor,
    iconName = step.iconName,
    _step$level = step.level,
    level = _step$level === void 0 ? 'L1' : _step$level,
    connectorColor = step.connectorColor,
    shouldBlink = step.shouldBlink,
    progressDuration = step.progressDuration;
  var configProps = stepperProps === null || stepperProps === void 0 ? void 0 : stepperProps.config;
  var stepStates = (_b = (_a = configProps === null || configProps === void 0 ? void 0 : configProps[size]) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b[state];
  var height = showMultiLevelStepper ? isLargeScreen ? (_d = (_c = configProps === null || configProps === void 0 ? void 0 : configProps.multiLevelStepperSize) === null || _c === void 0 ? void 0 : _c.desktop) === null || _d === void 0 ? void 0 : _d[level] : (_f = (_e = configProps === null || configProps === void 0 ? void 0 : configProps.multiLevelStepperSize) === null || _e === void 0 ? void 0 : _e.mobile) === null || _f === void 0 ? void 0 : _f[level] : (_g = configProps === null || configProps === void 0 ? void 0 : configProps[size]) === null || _g === void 0 ? void 0 : _g.stepperSize;
  var isDisabled = state === 'disabled';
  var isStateInprogressOrDisabled = state === 'in-progress' || isDisabled;
  var isDefaultState = state === 'default';
  var _icon = iconName || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.iconName);
  var stepProgressColor = progressColor || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.progressColor);
  var stepProgressPercent = progressPercent || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.progressPercent);
  var backgroundColor = stepsBackgroundColor || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.stepsBackgroundColor);
  var iconFontSize = height - (size == 'sm' ? 12 : 9);
  var counterColor = stepStates === null || stepStates === void 0 ? void 0 : stepStates.counterColor;
  var borderColor = stepStates === null || stepStates === void 0 ? void 0 : stepStates.borderColor;
  var _useState = useState(Object.assign(Object.assign({}, stepperStyles === null || stepperStyles === void 0 ? void 0 : stepperStyles.stepperConnecter), level === 'L1' && !isLastChild && {
      height: lastItemConnectorHeight,
      flexGrow: 0
    })),
    _style = _useState[0],
    setStyle = _useState[1];
  var _connectorColor = connectorColor || (stepStates === null || stepStates === void 0 ? void 0 : stepStates.connectorColor) || stepperProps.stepperConnectorColor;
  var blinkAnim = useRef(new Animated.Value(1)).current;
  var heightAnim = useRef(new Animated.Value(0)).current;
  var showConnector = isLastChild;
  useEffect(() => {
    if (showAnimation) {
      Animated.timing(heightAnim, {
        toValue: 1,
        duration: 2000,
        easing: Easing.linear,
        useNativeDriver: true
      }).start();
    }
  }, []);
  useEffect(() => {
    if (showAnimation) {
      var timer = setTimeout(() => {
        var newStyle = Object.assign(Object.assign({}, stepperStyles.multiLevelStepperConnector), state !== 'upcoming' && {
          backgroundColor: _connectorColor,
          flex: heightAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [0, 1]
          })
        });
        if (level === 'L1' && !isLastChild) {
          Object.assign(newStyle, {
            height: lastItemConnectorHeight,
            flexGrow: 0
          });
        }
        setStyle(newStyle);
      }, delay || 0);
      return () => clearTimeout(timer);
    }
  }, []);
  useEffect(() => {
    if (!shouldBlink) return;
    Animated.loop(Animated.sequence([Animated.timing(blinkAnim, {
      toValue: 0.2,
      duration: 800,
      easing: Easing.linear,
      useNativeDriver: true
    }), Animated.timing(blinkAnim, {
      toValue: 1,
      duration: 500,
      easing: Easing.linear,
      useNativeDriver: true
    })])).start();
    return () => {
      blinkAnim.stopAnimation();
    };
  }, [shouldBlink]);
  return _jsx(View, Object.assign({
    style: {
      flexDirection: 'row'
    }
  }, {
    children: _jsxs(View, Object.assign({
      style: {
        flexDirection: 'column',
        alignItems: 'center'
      }
    }, {
      children: [_jsxs(Animated.View, Object.assign({
        style: [stepperStyles.step, getStepContainerStyles(level !== 'L1' && state == 'upcoming' ? LILAC_700 : backgroundColor, borderColor, state === 'upcoming', isStateInprogressOrDisabled, isDisabled, level, props, isLargeScreen), Object.assign({}, level !== 'L1' && {
          opacity: blinkAnim
        }), style]
      }, getTestId(testID), {
        children: [state === 'loading' && _jsx(Progress, Object.assign({}, showMultiLevelStepper ? {
          customSize: height
        } : {
          size
        }, {
          value: stepProgressPercent,
          theme: theme,
          progressColor: stepProgressColor,
          progressBackgroundColor: theme == 'white' ? BLUE_GREY_200 : LILAC_600
        })), (isDisabled || isDefaultState) && _jsx(Typography, Object.assign({
          text: stepNumber,
          type: "body-para-sm-bold",
          color: counterColor
        }, getTestId(testID, 'stepNumber'))), state == 'upcoming' && _jsx(Typography, Object.assign({
          text: stepNumber,
          type: "body-para-sm-bold",
          color: counterColor
        }, getTestId(testID, 'stepNumber'))), state == 'in-progress' && _jsx(Progress, Object.assign({}, showMultiLevelStepper ? {
          customSize: height
        } : {
          size
        }, {
          value: stepProgressPercent,
          type: "circular",
          isRadialCircularProgress: true,
          progressBackgroundColor: GREEN_400,
          borderColor: GREEN_400,
          duration: progressDuration,
          iconName: level == 'L1' && 'CheckboxIcon'
        })), !!_icon && level == 'L1' && _jsx(DynamicSvg, Object.assign({
          iconName: _icon,
          color: WHITE,
          fontSize: iconFontSize
        }, getTestId(testID, 'icon')))]
      })), showConnector && _jsxs(View, Object.assign({
        style: [stepperStyles.connectorContainer, _style, {
          borderWidth: 0
        }]
      }, getTestId(testID, level + "_connector"), {
        children: [_jsx(View, {
          style: [stepperStyles.stepperConnecter]
        }), _jsx(Animated.View, Object.assign({
          style: [_style, stepperStyles.animatedConnector]
        }, getTestId(testID, 'stepperConnector')))]
      }))]
    }))
  }));
};